import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { templateData } from '../../data/data_templates';
import './MeetingPreview.css';

function MeetingPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const [meetingData, setMeetingData] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    objectives: false,
    focuses: false,
    conclusions: false
  });

  useEffect(() => {
    if (location.state?.meetingData) {
      setMeetingData(location.state.meetingData);
      loadTemplateData(location.state.meetingData.instance_reference);
    } else {
      setError('No meeting data provided');
      setLoading(false);
    }
  }, []);

  const loadTemplateData = async (templateId) => {
    if (!templateId) {
      setError('No template reference found for this meeting');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await templateData.getCompleteTemplate(templateId);
      
      if (response.success) {
        setTemplateInfo(response.template);
      } else {
        setError('Failed to load template data');
      }
    } catch (error) {
      setError('Failed to load template data');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleStartMeeting = () => {
    // Navigate to the meeting page with the full meeting data in state
    navigate('/meeting', { 
      state: { 
        instanceId: meetingData.instance_id, 
        meetingData: meetingData 
      } 
    });
  };



  if (loading) {
    return (
      <div className="meeting-preview-container">
        <div className="preview-content-wrapper">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading meeting details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="meeting-preview-container">
        <div className="preview-content-wrapper">
          <div className="error-container">
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!meetingData || !templateInfo) {
    return (
      <div className="meeting-preview-container">
        <div className="preview-content-wrapper">
          <div className="error-container">
            <h2>No Data Available</h2>
            <p>Unable to load meeting or template information.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="meeting-preview-container">
      <div className="preview-content-wrapper">
        <div className="preview-header">
          <h1>Meeting Preview</h1>
        </div>

        <div className="preview-content">
        {/* Meeting Details Section */}
        <div className="meeting-details-section">
          <h2>
            {meetingData.name} ({meetingData.short_id || 'N/A'})
            {meetingData.training_id && (
              <span className="training-pill">Training</span>
            )}
          </h2>
          {meetingData.description && (
            <p className="meeting-description">{meetingData.description}</p>
          )}
        </div>

        {/* Template Details Section */}
        <div className="template-details-section">
          <h3>Template: {templateInfo.name}</h3>
          <p className="template-description">{templateInfo.description}</p>
          
          {/* Objectives Section */}
          <div className="collapsible-section">
            <button 
              className="section-header"
              onClick={() => toggleSection('objectives')}
            >
              <span>Objectives of the Meeting</span>
              <span className={`expand-icon ${expandedSections.objectives ? 'expanded' : ''}`}>
                ▼
              </span>
            </button>
                         {expandedSections.objectives && (
               <div className="section-content">
                 {templateInfo.objectives && templateInfo.objectives.length > 0 ? (
                   templateInfo.objectives.map((objective) => (
                     <div key={objective.objectives_id} className="objective-item">
                       <h4>{objective.name}</h4>
                       <p>{objective.description}</p>
                       {objective.tasks && objective.tasks.length > 0 && (
                         <div className="tasks-list">
                           <h5>Tasks:</h5>
                           <ul>
                             {objective.tasks.map((task) => (
                               <li key={task.id}>
                                 <strong>{task.name}</strong>
                                 {task.description && (
                                   <span className="task-description"> - {task.description}</span>
                                 )}
                               </li>
                             ))}
                           </ul>
                         </div>
                       )}
                     </div>
                   ))
                 ) : (
                   <div className="empty-section-message">
                     <p>No objectives defined for this template.</p>
                   </div>
                 )}
               </div>
             )}
          </div>

          {/* Focuses Section */}
          <div className="collapsible-section">
            <button 
              className="section-header"
              onClick={() => toggleSection('focuses')}
            >
              <span>Purpose of the Meeting</span>
              <span className={`expand-icon ${expandedSections.focuses ? 'expanded' : ''}`}>
                ▼
              </span>
            </button>
                         {expandedSections.focuses && (
               <div className="section-content">
                 {templateInfo.focuses && templateInfo.focuses.length > 0 ? (
                   templateInfo.focuses.map((focus) => (
                     <div key={focus.id} className="focus-item">
                       <h4>{focus.name}</h4>
                       <p>{focus.description}</p>
                     </div>
                   ))
                 ) : (
                   <div className="empty-section-message">
                     <p>No purpose defined for this template.</p>
                   </div>
                 )}
               </div>
             )}
          </div>

          {/* Conclusions Section */}
          <div className="collapsible-section">
            <button 
              className="section-header"
              onClick={() => toggleSection('conclusions')}
            >
              <span>Outcome of the Meeting</span>
              <span className={`expand-icon ${expandedSections.conclusions ? 'expanded' : ''}`}>
                ▼
              </span>
            </button>
                         {expandedSections.conclusions && (
               <div className="section-content">
                 {templateInfo.conclusions && templateInfo.conclusions.length > 0 ? (
                   templateInfo.conclusions.map((conclusion) => (
                     <div key={conclusion.id} className="conclusion-item">
                       <h4>{conclusion.name}</h4>
                       <p>{conclusion.description}</p>
                     </div>
                   ))
                 ) : (
                   <div className="empty-section-message">
                     <p>No outcomes defined for this template.</p>
                   </div>
                 )}
               </div>
             )}
          </div>
        </div>

        {/* Start Meeting Button */}
        <div className="start-meeting-section">
          <button 
            onClick={handleStartMeeting}
            className="start-meeting-btn"
          >
            Start Meeting
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default MeetingPreview;
